// /reset-password/:token — set a new password from an emailed reset link. On
// success the backend revokes existing sessions, so we send the user to sign in
// fresh with the new password.
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "./auth-layout";
import { Alert, Button, Input } from "@/components/ui";
import { resetPassword } from "@/api/auth";
import { ApiError } from "@/api/client";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("Password must be at least 8 characters."));
      return;
    }
    if (password !== confirm) {
      setError(t("Passwords don't match."));
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("This reset link is invalid or has expired. Request a new one."));
      } else {
        setError(err instanceof Error ? err.message : t("Something went wrong. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t("Choose a new password")}
      subtitle={done ? t("Your password has been updated.") : t("Pick a strong, new password.")}
      footer={
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          {t("Back to sign in")}
        </Link>
      }
    >
      {done ? (
        <div className="space-y-4">
          <Alert tone="info">{t("All set — sign in with your new password.")}</Alert>
          <Button size="lg" className="w-full" onClick={() => navigate("/login", { replace: true })}>
            {t("Sign in")}
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Input
            label={t("New password")}
            name="new_password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("At least 8 characters")}
          />
          <Input
            label={t("Confirm new password")}
            name="confirm_password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
          />
          <Button type="submit" size="lg" loading={submitting} className="w-full">
            {t("Update password")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
